<template>
  <div class="text-left">
    <div class="mt-6 justify-center items-center w-full max-w-96">
      <div class="flex flex-col">

        <label for="enterName">Change username</label>
        <input id="enterName" @keyup.enter="submitName" v-model="name" type="text"
               placeholder="Enter username"
               class="p-2 border-2 border-blue-200 rounded w-full">

        <p v-if="showLettersError" class="text-red-300">Must only contain letters</p>
        <p v-if="showLengthError" class="text-red-300">Must be more than 2 letters</p>
        <p v-if="submitError" class="text-red-300">{{submitError}}</p>
        <button @click="submitName" :disabled="!canSave"
                class="bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 mt-1 text-gray-900 text-xl py-2 disabled:opacity-40 disabled:cursor-not-allowed">
          Save
        </button>

        <div class="flex flex-row items-center justify-between mt-6">
          <label for="sound">Sound effects</label>
          <button id="sound" role="switch" :aria-checked="!muted" @click="toggleMute"
                  class="relative w-12 h-7 rounded-full transition-colors"
                  :class="muted ? 'bg-gray-600' : 'bg-green-400'">
            <span class="absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform"
                  :class="{ 'translate-x-5': !muted }"></span>
          </button>
        </div>

        <!-- Account -->
        <div class="border-t border-gray-700 mt-6 pt-4">
          <div v-if="isAuthenticated">
            <p class="text-sm text-gray-400">Signed in{{ accountName ? ` as ${accountName}` : '' }}</p>
            <button @click="signout" class="border-2 border-green-400 rounded mt-2 w-full text-white text-xl py-2 bg-gray-900">
              Logout
            </button>
          </div>
          <div v-else class="rounded-lg bg-gradient-to-r from-green-400/15 to-teal-400/15 ring-1 ring-green-400/30 p-3">
            <p class="font-semibold">Playing as a guest</p>
            <p class="text-sm text-gray-300 mt-1">Log in to save your progress and earn <span class="text-gradient font-extrabold">5&times; XP</span>.</p>
            <button @click="loginWithRedirect()"
                    class="bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 rounded mt-3 w-full text-gray-900 text-xl py-2">
              Log in
            </button>
          </div>
        </div>

        <!-- How to play -->
        <div class="border-t border-gray-700 mt-6 pt-4">
          <button @click="showHowTo = !showHowTo"
                  class="flex w-full items-center justify-between bg-transparent border-0 p-0 text-white">
            <span>How to play</span>
            <span class="text-xs text-gray-400 transition-transform" :class="{ 'rotate-180': showHowTo }">&#9662;</span>
          </button>
          <div v-if="showHowTo" class="mt-3">
            <GameDescription/>
          </div>
        </div>

        <!-- Feedback -->
        <div class="border-t border-gray-700 mt-6 pt-4 space-y-3">
          <div class="flex flex-row items-center justify-between">
            <label for="issue">Experiencing an issue?</label>
            <a id="issue" class="text-green-400 underline" target="_blank" rel="noopener"
               href="https://typebot.co/syndles-issue-hk0noj8">Send Issue</a>
          </div>
          <div class="flex flex-row items-center justify-between">
            <label for="idea">Got a wicked idea?</label>
            <a id="idea" class="text-green-400 underline" target="_blank" rel="noopener"
               href="https://typebot.co/syndles-ooxwugf">Send Idea</a>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>
<script setup>

import {computed, onMounted, ref} from "vue";
import {useUserState} from "../composables/useUserState.js";
import {useName} from "../composables/useName.js";
import {useSound} from "../composables/useSound.js";
import {useAuth0} from "@auth0/auth0-vue";
import GameDescription from "./GameDescription.vue";

const emit = defineEmits(['saved'])

const showHowTo = ref(false);

const { isAuthenticated, logout, loginWithRedirect, user } = useAuth0();
const { isNameValid, name, showLengthError, showLettersError, submitError} = useName();
const {updateUser} = useUserState();
const {muted, toggleMute} = useSound();

const originalName = ref('');

onMounted(() => {
  let storedName = localStorage.getItem('name');
  if (storedName) {
    name.value = storedName;
    originalName.value = storedName;
  }
})

// only allow saving a valid name that's actually different from the current one
const canSave = computed(() => isNameValid.value && name.value.trim() !== originalName.value.trim());

// prefer the auth0 profile name, fall back to email
const accountName = computed(() => user.value?.name || user.value?.email || '');

async function submitName() {
    if (canSave.value) {
      localStorage.setItem('name', name.value.trim());
      submitError.value = await updateUser(name.value.trim());
      if (!submitError.value) {
        emit('saved');
      }
    }
}

function signout() {
  logout({ logoutParams: { returnTo: window.location.origin } });
  localStorage.clear();
}
</script>


<style scoped>

</style>